import { inject, Injectable } from '@angular/core';
import { isDefined } from '@sama/js-core';
import { InjectableSuperclass } from '@sama/ng-core';
import { size } from 'lodash';
import { KeyboardShortcutName } from '../../../../../../cross-team-shared/misc-services/keyboard-shortcut.service';
import { EventTrackingValueWithSource } from '../../../../../../cross-team-shared/misc-utils';
import { toggleState } from '../../../../../../shared/misc-utilities/store-utils';
import { SettingsService } from '../../../../../h3ml/dsl/sensor-fusion/view-settings/settings.service';
import { TaskWorkspaceStore } from '../../../../../h3ml/state/task-workspace-store';
import { FusionManager } from '../../fusion/fusion-manager';
import { OutputKeyboardShortcutService } from '../../utils/output-keyboard-shortcut.service';

@Injectable()
export class ObjectlessToolbarKeyboardShortcutService extends InjectableSuperclass {
  private readonly taskWorkspaceStore = inject(TaskWorkspaceStore);
  private readonly settingsStore = this.taskWorkspaceStore('settings');

  private outputKeyboardShortcutService = inject(OutputKeyboardShortcutService);
  private settingsService = inject(SettingsService);
  private fusionManager = inject(FusionManager);

  register3D() {
    this.setUpResetSensorView();
    this.setUpToggleInspect();
    this.setUpViewMode();

    this.setUpToggleOrthographicCamera();
    this.setUpToggleEnhancedPointCloud();
  }

  register2D() {
    this.setUpResetSensorView();
    this.setUpToggleInspect();
    this.setUpViewMode();
  }

  private setUpToggleOrthographicCamera() {
    this.setUpSubscriptionToShortcut(
      'toggleOrthographicCamera',
      (eventTrackingValue) => {
        const isSplitViewActive = isDefined(
          this.taskWorkspaceStore.state().splitViewOutputName,
        );

        if (!isSplitViewActive) {
          this.settingsService.toggleOrthographicCamera(eventTrackingValue);
          return;
        }

        if (this.hasPointCloudShapeFocused()) {
          const topViewHiddenStore = this.settingsStore(
            'topViewHiddenInSplitView',
          );
          toggleState(topViewHiddenStore);
          this.settingsService.trackMainCameraTypeEvent({
            ...eventTrackingValue,
            new_value: topViewHiddenStore.state()
              ? 'perspective'
              : 'shape_orthographic',
          });
        } else {
          this.settingsService.toggleOrthographicCamera(eventTrackingValue);
        }
      },
    );
  }

  private setUpResetSensorView() {
    this.setUpSubscriptionToShortcut(
      'resetSensorView',
      (eventTrackingValue) => {
        this.settingsService.resetSensorView(eventTrackingValue);
      },
    );
  }

  private setUpViewMode() {
    this.setUpSubscriptionToShortcut('cycleViewMode', (eventTrackingValue) => {
      this.settingsService.toggleViewMode(eventTrackingValue);
    });
  }

  private setUpToggleInspect() {
    this.setUpSubscriptionToShortcut(
      'toggleInspectMode',
      (eventTrackingValue) => {
        this.settingsService.toggleInspectMode(eventTrackingValue);
      },
    );
  }

  private setUpToggleEnhancedPointCloud() {
    this.setUpSubscriptionToShortcut(
      'toggleEnhancedPointCloud',
      (eventTrackingValue) => {
        this.settingsService.toggleEnhancedPointCloud(eventTrackingValue);
      },
    );
  }

  private setUpSubscriptionToShortcut(
    shortcutName: KeyboardShortcutName,
    handleEventCallback: (
      eventTrackingValue: EventTrackingValueWithSource,
    ) => void,
  ) {
    this.subscribeTo(
      this.outputKeyboardShortcutService.getEvent$(shortcutName),
      (event) => handleEventCallback(event.eventTrackingValue),
    );
  }

  private hasPointCloudShapeFocused() {
    const minion = this.fusionManager.getPrimaryMinion();
    return size(minion.shapeSelectionService.getSelectionForOutput()) === 1;
  }
}
